import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal } from "lucide-react";
import { api } from "../../lib/api";

// Dedicated Commodities config editor — same generic values/defaults shape
// every admin config section already uses (`GET/POST /admin/config/{section}`
// is section-agnostic infrastructure, not a Stocks screen), scoped to only
// the 5 commodity_* sections so admins never see Stocks sections here.
const SECTIONS = [
  { key: "commodity_autopilot", label: "Autopilot — Combined Scan + Paper Trading" },
  { key: "commodity_risk", label: "Risk — Target Profit / Stop-Loss (Rs) & Cooldown" },
  { key: "commodity_options", label: "Options Pricing — Black-76 Assumptions (shared)" },
  { key: "commodity_reversal", label: "Reversal Entry Zones" },
  { key: "commodity_vwap_momentum", label: "MCX VWAP Momentum" },
  // 2026-08-22 — order-shape settings for real live orders (quantity per
  // underlying, daily cap, product/order type). The ON/OFF kill-switch
  // itself lives on the Execution screen, not here — see commodity_
  // autopilot.py's is_live_trading_enabled/set_live_trading.
  // 2026-10-02 — relabelled: this is the retired Upstox order path (never
  // read since live MCX orders moved to Kite). Real Kite orders use
  // Admin → Configuration → "Section 13 — Kite Execution (Crude Oil)".
  { key: "commodity_live_trading", label: "LEGACY Upstox Order Shape — NOT USED (Kite: Admin › Section 13)" },
];

const ACRONYMS = new Set(["rvol", "atr", "iv", "otm", "dte", "sl", "tp", "rr", "mcx", "vwap", "ce", "pe"]);
const prettyLabel = (k) =>
  k.split("_").map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");

// 2026-08-21 — per-underlying numeric settings (limit_order_offset,
// strike_interval, implied_volatility, rvol_threshold_by_symbol) used to
// render as a raw JSON textarea, which is exactly the kind of field the
// "make Limit order / Target Profit configurable in frontend" ask was about
// — clean per-symbol number inputs instead, plus an add/remove affordance
// for underlyings not already in the default set.
const NumericDictEditor = ({ value, onChange }) => {
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");
  const dict = value && typeof value === "object" ? value : {};
  const entries = Object.entries(dict);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {entries.map(([sym, val]) => (
          <div key={sym} className="space-y-0.5">
            <div className="flex items-center justify-between">
              <label className="text-[9px] uppercase tracking-[0.15em] text-muted-3 font-mono-data">{sym}</label>
              <button
                type="button"
                onClick={() => {
                  const next = { ...dict };
                  delete next[sym];
                  onChange(next);
                }}
                data-testid={`numeric-dict-remove-${sym}`}
                className="text-[10px] text-muted-3 hover:opacity-100 opacity-60"
                title={`Remove ${sym}`}
              >
                ×
              </button>
            </div>
            <input
              type="number"
              step="0.01"
              value={val}
              onChange={(e) => onChange({ ...dict, [sym]: parseFloat(e.target.value) })}
              data-testid={`numeric-dict-value-${sym}`}
              className="w-full bg-transparent border border-grid px-2 py-1.5 text-xs font-mono-data"
            />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <input
          type="text"
          placeholder="SYMBOL, e.g. GOLDM"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value.toUpperCase())}
          data-testid="numeric-dict-new-symbol"
          className="bg-transparent border border-grid px-2 py-1 text-[11px] font-mono-data w-36"
        />
        <input
          type="number"
          step="0.01"
          placeholder="value"
          value={newVal}
          onChange={(e) => setNewVal(e.target.value)}
          data-testid="numeric-dict-new-value"
          className="bg-transparent border border-grid px-2 py-1 text-[11px] font-mono-data w-20"
        />
        <button
          type="button"
          onClick={() => {
            const trimmed = newKey.trim();
            if (!trimmed || newVal === "") return;
            onChange({ ...dict, [trimmed]: parseFloat(newVal) });
            setNewKey("");
            setNewVal("");
          }}
          data-testid="numeric-dict-add"
          className="px-2 py-1 border border-grid text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/[0.04]"
        >
          + Add
        </button>
      </div>
    </div>
  );
};

const CommoditiesConfiguration = () => {
  const [section, setSection] = useState("commodity_autopilot");
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/config/${section}`);
      setCfg(data);
      setEditing(data.values || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load config");
    }
  }, [section]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const values = {};
    Object.entries(cfg?.defaults || {}).forEach(([k, defaultVal]) => {
      const v = editing[k];
      if (Array.isArray(defaultVal)) {
        values[k] = typeof v === "string"
          ? v.split(",").map((s) => s.trim()).filter(Boolean)
          : (Array.isArray(v) ? v : (defaultVal || []));
      } else if (defaultVal !== null && typeof defaultVal === "object") {
        // Nested per-underlying dicts (limit_order_offset, strike_interval,
        // implied_volatility, rvol_threshold_by_symbol) — numeric ones are
        // edited as real objects via NumericDictEditor below; anything else
        // falls back to JSON text (kept for forward-compatibility, though no
        // current commodity_* field takes this path).
        if (v && typeof v === "object") {
          values[k] = v;
        } else if (typeof v === "string") {
          try {
            values[k] = JSON.parse(v);
          } catch {
            values[k] = defaultVal;
          }
        } else {
          values[k] = defaultVal;
        }
      } else {
        values[k] = v ?? defaultVal;
      }
    });
    try {
      const { data } = await api.post(`/admin/config/${section}`, {
        values,
        enabled: cfg?.enabled ?? true,
      });
      setCfg({ ...cfg, ...data });
      toast.success(`${section} config saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${section} to default values?`)) return;
    const { data } = await api.post(`/admin/config/${section}/reset`);
    setCfg({ ...cfg, values: data.values, enabled: true });
    setEditing(data.values);
    toast.success("Reset to defaults");
  };

  return (
    <section className="space-y-4" data-testid="commodities-config-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Commodities · MCX · Admin
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <SlidersHorizontal size={20} /> Configuration
          </h2>
        </div>
        <select
          value={section}
          onChange={(e) => setSection(e.target.value)}
          data-testid="commodities-config-section"
          className="bg-transparent border border-grid px-3 py-1.5 text-xs"
        >
          {SECTIONS.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>

      {!cfg ? (
        <div className="text-muted-2 font-mono-data text-xs">Loading…</div>
      ) : (
        <div className="border border-grid p-5 space-y-4">
          <div className="flex justify-between items-center">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
              Last updated:{" "}
              {cfg.updated_at
                ? new Date(cfg.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
                : "never (using defaults)"}
            </div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-mono-data flex items-center gap-2">
              <input
                type="checkbox"
                checked={cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                data-testid="commodities-config-enabled"
              />
              Section enabled
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(cfg.defaults || {}).map(([k, defaultVal]) => {
              const isString = typeof defaultVal === "string";
              const isList = Array.isArray(defaultVal);
              const isObject = !isList && defaultVal !== null && typeof defaultVal === "object";
              const isNumericDict = isObject && Object.values(defaultVal).every((v) => typeof v === "number");
              const currentVal = editing[k] ?? defaultVal;

              return (
                <div key={k} className={`space-y-1 ${isObject ? "md:col-span-2" : ""}`}>
                  <label className="text-xs font-heading" title={k}>{prettyLabel(k)}</label>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-muted-3 font-mono-data">
                    {k}{" "}
                    <span className="normal-case">
                      (default {isList ? `[${defaultVal.join(", ")}]` : isObject ? JSON.stringify(defaultVal)
                        : isString ? `"${defaultVal}"` : String(defaultVal)})
                    </span>
                  </div>
                  {isNumericDict ? (
                    <NumericDictEditor
                      value={typeof currentVal === "object" && currentVal !== null ? currentVal : defaultVal}
                      onChange={(next) => setEditing({ ...editing, [k]: next })}
                    />
                  ) : isObject ? (
                    <textarea
                      value={typeof currentVal === "string" ? currentVal : JSON.stringify(currentVal, null, 2)}
                      onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                      data-testid={`commodities-config-${k}`}
                      rows={4}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-[11px] font-mono-data"
                    />
                  ) : isList ? (
                    <input
                      type="text"
                      value={typeof currentVal === "string" ? currentVal : (Array.isArray(currentVal) ? currentVal.join(", ") : "")}
                      onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                      data-testid={`commodities-config-${k}`}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                      placeholder="Comma-separated, e.g. CRUDEOIL, SILVERM"
                    />
                  ) : isString ? (
                    <input
                      type="text"
                      value={currentVal}
                      onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                      data-testid={`commodities-config-${k}`}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                    />
                  ) : typeof defaultVal === "boolean" ? (
                    <select
                      value={String(currentVal)}
                      onChange={(e) => setEditing({ ...editing, [k]: e.target.value === "true" })}
                      data-testid={`commodities-config-${k}`}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  ) : (
                    <input
                      type="number"
                      step="0.01"
                      value={currentVal}
                      onChange={(e) => setEditing({ ...editing, [k]: parseFloat(e.target.value) })}
                      data-testid={`commodities-config-${k}`}
                      className="w-full bg-transparent border border-grid px-3 py-2 text-sm font-mono-data"
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex gap-3">
            <button
              onClick={save}
              data-testid="commodities-config-save"
              className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data"
              style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
            >
              Save
            </button>
            <button
              onClick={reset}
              data-testid="commodities-config-reset"
              className="px-4 py-2 border border-grid text-[11px] uppercase tracking-[0.2em] font-mono-data"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default CommoditiesConfiguration;
